import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, Building2, FileText, CheckCircle2, AlertTriangle, 
  ExternalLink, Download, Copy, ClipboardList, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const GovJobsPage = () => {
  const navigate = useNavigate();
  const [cvs, setCvs] = useState([]);
  const [selectedCvId, setSelectedCvId] = useState('');
  const [z83Data, setZ83Data] = useState(null);
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('guide');

  useEffect(() => {
    fetchCVs();
    fetchGuide();
  }, []);

  const fetchCVs = async () => {
    try {
      const response = await api.get('/cv/list');
      setCvs(response.data.cvs || []);
      if (response.data.cvs?.length > 0) {
        setSelectedCvId(response.data.cvs[0].id);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    }
  };

  const fetchGuide = async () => {
    try {
      const response = await api.get('/z83/guide');
      setGuide(response.data);
    } catch (error) {
      console.error('Error fetching guide:', error);
    }
  };

  const exportZ83 = async () => {
    if (!selectedCvId) {
      toast.error('Please select a CV first');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/z83/export', { cv_id: selectedCvId });
      setZ83Data(response.data);
      setActiveTab('export');
      toast.success('CV data exported for Z83 form!');
    } catch (error) {
      toast.error('Failed to export CV data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const renderGuide = () => (
    <div className="space-y-6">
      {guide && (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Important</h3>
                <p className="text-amber-700 text-sm">{guide.intro}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            {guide.steps.map((step) => (
              <Card key={step.step} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                      <p className="text-slate-600 text-sm mb-2">{step.description}</p>
                      <ul className="space-y-1">
                        {step.tips.map((tip, idx) => (
                          <li key={idx} className="text-sm text-slate-500 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-800 text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Common Mistakes to Avoid
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid md:grid-cols-2 gap-2">
                {guide.common_mistakes.map((mistake, idx) => (
                  <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    {mistake}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Useful Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {guide.useful_links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {link.name}
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );

  const renderExport = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Select CV to Export
          </CardTitle>
          <CardDescription>Choose which CV to use for your Z83 application</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Select value={selectedCvId} onValueChange={setSelectedCvId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a CV" />
                </SelectTrigger>
                <SelectContent>
                  {cvs.map((cv) => (
                    <SelectItem key={cv.id} value={cv.id}>
                      {cv.cv_data?.personal_details?.full_name || 'Unnamed CV'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={exportZ83} disabled={loading || !selectedCvId}>
              {loading ? 'Exporting...' : 'Export for Z83'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {z83Data && (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <p className="text-emerald-800">
                CV data exported for <strong>{z83Data.cv_name}</strong>. Copy these details to your Z83 form.
              </p>
            </div>
          </div>

          {/* Section B - Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section B - Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(z83Data.z83_data.section_b).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium text-slate-900">{value || '—'}</p>
                    </div>
                    {value && (
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(value)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Section C - Qualifications */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section C - Qualifications</CardTitle>
            </CardHeader>
            <CardContent>
              {z83Data.z83_data.section_c_qualifications.length > 0 ? (
                <div className="space-y-3">
                  {z83Data.z83_data.section_c_qualifications.map((qual, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900">{qual.qualification}</p>
                          <p className="text-sm text-slate-600">{qual.institution}</p>
                          <p className="text-sm text-slate-500">{qual.field_of_study} • {qual.year_obtained}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${qual.qualification}, ${qual.institution}, ${qual.year_obtained}`)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No qualifications added to CV</p>
              )}
            </CardContent>
          </Card>

          {/* Section D - Experience */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section D - Work Experience</CardTitle>
            </CardHeader>
            <CardContent>
              {z83Data.z83_data.section_d_experience.length > 0 ? (
                <div className="space-y-3">
                  {z83Data.z83_data.section_d_experience.map((exp, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900">{exp.position}</p>
                          <p className="text-sm text-slate-600">{exp.employer}</p>
                          <p className="text-sm text-slate-500">{exp.start_date} - {exp.end_date}</p>
                          {exp.duties && <p className="text-sm text-slate-600 mt-2">{exp.duties}</p>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${exp.position} at ${exp.employer}`)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No work experience added to CV</p>
              )}
            </CardContent>
          </Card>

          {/* Section E - References */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section E - References</CardTitle>
            </CardHeader>
            <CardContent>
              {z83Data.z83_data.section_e_references.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-3">
                  {z83Data.z83_data.section_e_references.map((ref, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                      <p className="font-semibold text-slate-900">{ref.name}</p>
                      <p className="text-sm text-slate-600">{ref.relationship}</p>
                      <p className="text-sm text-slate-500">{ref.telephone}</p>
                      <p className="text-sm text-slate-500">{ref.email}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No references added to CV</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-emerald-600" />
                Government Job Helper
              </h1>
              <p className="text-sm text-slate-500">Z83 Application Guide & Export</p>
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">SA Government Jobs</Badge>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('guide')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'guide' 
                  ? 'border-emerald-600 text-emerald-600' 
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <BookOpen className="w-4 h-4 inline mr-2" />
              Step-by-Step Guide
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'export' 
                  ? 'border-emerald-600 text-emerald-600' 
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <ClipboardList className="w-4 h-4 inline mr-2" />
              Export CV for Z83
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-8">
        {activeTab === 'guide' ? renderGuide() : renderExport()}
      </main>
    </div>
  );
};

export default GovJobsPage;
